/**
 * RotationOverlay Component
 *
 * Provides visual guidance for dice rotation during registration capture:
 * - Center crosshair to help keep dice centered
 * - Face number ring showing visible side faces (replaces clock markers)
 * - Toggle button to show/hide overlay
 */

import { DiceType } from '@dice-capture/shared/types/dice-registry';
import { FaceNumberRing } from './FaceNumberRing';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Props for RotationOverlay component
 */
export interface RotationOverlayProps {
  /** Whether the overlay is visible */
  isVisible: boolean;
  /** Toggle overlay visibility */
  onToggle: () => void;
  /** Current rotation position (0-based index) */
  currentPosition: number;
  /** Total number of rotation positions */
  totalPositions: number;
  /** Current face number being captured */
  currentFace: number;
  /** Total faces for the dice type */
  totalFaces: number;
  /** Dice type for face number ring guide (null shows fallback clock markers) */
  diceType: DiceType | null;
  /** Additional CSS classes */
  className?: string;
}

/** SVG Crosshair component for centering guidance */
function Crosshair() {
  const lineProps = { stroke: 'white', strokeWidth: '0.5', strokeOpacity: '0.6' };
  return (
    <svg data-testid="crosshair" className="absolute inset-0 w-full h-full"
      viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
      <line x1="30" y1="50" x2="70" y2="50" {...lineProps} />
      <line x1="50" y1="30" x2="50" y2="70" {...lineProps} />
      <circle cx="50" cy="50" r="3" fill="none" {...lineProps} />
    </svg>
  );
}

/** Clock position markers at 12, 3, 6, 9 o'clock around the edge */
function ClockMarkers() {
  const positions = [0, 90, 180, 270]; // Angles in degrees from top
  return (
    <div data-testid="clock-markers" className="absolute inset-0">
      {positions.map((angle) => {
        const radians = (angle - 90) * (Math.PI / 180);
        const x = 50 + 45 * Math.cos(radians);
        const y = 50 + 45 * Math.sin(radians);
        return (
          <div key={angle} className="absolute w-6 h-6 flex items-center justify-center"
            style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}>
            <div className="w-2 h-2 rounded-full bg-white/40 border border-white/60" />
          </div>
        );
      })}
    </div>
  );
}

/** RotationOverlay provides visual guidance during dice rotation capture */
export function RotationOverlay({
  isVisible, onToggle, currentPosition, totalPositions, currentFace, diceType, className
}: RotationOverlayProps) {
  return (
    <div className={cn('absolute inset-0', className)}>
      <Button variant="ghost" size="sm" onClick={onToggle} data-testid="overlay-toggle"
        className="absolute bottom-20 right-2 z-10 bg-black/40 hover:bg-black/60 text-white/80 hover:text-white px-2 py-1 h-auto text-xs">
        {isVisible ? 'Hide Guide' : 'Show Guide'}
      </Button>
      {isVisible && (
        <div data-testid="rotation-overlay-content" className="absolute inset-0 pointer-events-none">
          <Crosshair />
          {diceType ? (
            <FaceNumberRing
              diceType={diceType}
              currentFace={currentFace}
              currentPosition={currentPosition}
              totalPositions={totalPositions}
            />
          ) : (
            <ClockMarkers />
          )}
        </div>
      )}
    </div>
  );
}

export default RotationOverlay;
